// Sugalarm — scaled chart with treatment + alert overlays.
//
// Same SVG approach as Sparkline (fixed viewBox + width:100%) so the
// chart fills its container fluidly. Y axis ticks at the four
// thresholds; X axis labelled with start/end times.
//
// Cycle 48 adds three opt-in capabilities used by the Now-screen's
// expanded chart (mobile + web only; kiosk never passes them so its
// behavior is byte-identical to cycle 47):
//   • `panSliceMs` — render only a `panSliceMs`-wide slice of the
//     [rangeStartMs, rangeEndMs] window, swipeable left/right.
//   • `showNowLine` / `nowMs` — a vertical dashed "now" marker.
//   • `onReadingTap` — each reading dot becomes a tap target that
//     surfaces the dot's screen rect so the parent can anchor a
//     detail popover.
// When `panSliceMs` is unset and no tap handler is passed the chart
// behaves exactly as it did in cycle 47.
//
// Cycle 65 layers four shape/gesture upgrades on top:
//   • Reading dots + treatment / alert glyphs render as ellipses with
//     an x-correction factor so they paint as TRUE CIRCLES regardless
//     of the chart's non-uniform x stretch (root-cause fix; see
//     `xCorrect`). Treatment glyphs (carbs triangle, insulin droplet)
//     and the alert ring stay their existing shapes — just unstretched.
//   • Pinch-to-zoom (opt-in via `pinchZoom`) — two-finger pinch on a
//     pannable slice mutates the slice width about the gesture
//     midpoint. Clamped to [MIN_SLICE_MS, fullSpan]; pan offset
//     re-clamped after every zoom.
//   • rAF-coalesced pan + zoom — pointermove handlers schedule a
//     single requestAnimationFrame to apply gesture state, so a fast
//     drag doesn't thrash React state per pointermove.
//   • `panSliceMs` is now the INITIAL slice width; internal sliceMs
//     state replaces it after a pinch. The prop is re-read whenever it
//     or the data window changes.
//
// Pinch never fires a dot tooltip / popover: the moment a second
// pointer lands the pending single-pointer gesture upgrades to pinch
// and the tap-suppress flag stays on. Single tap remains untouched.
//
// `touch-action: none` on the wrap when any gesture is enabled —
// native page scroll inside the chart is blocked so two-finger pinch
// reaches JS instead of being eaten by the browser's pinch-zoom.

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { displayValue, fmtTime, zoneColor, zoneOf } from "../../lib/glucose";
import {
  computeProjectionModel,
  isProjectionStale,
  predictedValue,
  projectedCenter,
} from "../../lib/projection";
import type {
  Alert,
  Reading,
  Thresholds,
  Treatment,
  Zone,
} from "../../lib/types";
import {
  MarkerGlyph,
  glyphKindForEvent,
  treatmentColor,
} from "./markerGlyphs";
import styles from "./HistoryChart.module.css";

// Cycle 57: lightweight tooltip helpers.
function alertLevelLabel(level: Alert["level"]): string {
  switch (level) {
    case "urgent_low":
      return "Urgent low";
    case "low":
      return "Low";
    case "high":
      return "High";
    case "urgent_high":
      return "Urgent high";
    default:
      return String(level);
  }
}

function treatmentTooltipBody(tr: Treatment, h24: boolean): string {
  const t = fmtTime(new Date(tr.timestamp).getTime(), { h24 });
  switch (tr.event_type) {
    case "carbs":
      return `Carbs · ${tr.carbs_g ?? 0} g · ${t}`;
    case "insulin":
      return `Insulin · ${tr.insulin_u ?? 0} u · ${t}`;
    case "bg_check":
      return `BG check · ${t}`;
    case "exercise":
      return `Exercise · ${t}`;
    case "note":
      return `Note · ${t}`;
    default:
      return `${tr.event_type} · ${t}`;
  }
}

interface Props {
  readings: Reading[];
  treatments: Treatment[];
  alerts: Alert[];
  thresholds: Thresholds;
  rangeStartMs: number;
  rangeEndMs: number;
  height?: number;
  h24?: boolean;
  units?: "mg/dL" | "mmol/L";
  // ── Cycle 48 ─────────────────────────────────────────────────────
  /** When set, render only a `panSliceMs`-wide slice of the full
   *  [rangeStartMs, rangeEndMs] window. The slice's right edge starts
   *  at `rangeEndMs` ("now") and the user swipes left to walk it into
   *  the past. Unset → whole window, no pan handlers (cycle-47).
   *  Cycle 65: now the INITIAL slice width — internal state owns the
   *  live slice after a pinch. Resetting the prop (window switch)
   *  re-seeds the internal slice. */
  panSliceMs?: number;
  /** Draw a vertical dashed line at `nowMs` when it falls inside the
   *  visible window. Default true. */
  showNowLine?: boolean;
  /** Drives the now-line position. Default `Date.now()`. */
  nowMs?: number;
  /** Make every reading dot a tap target. The callback receives the
   *  reading and the tapped hit-circle's screen rect so the parent
   *  can position a popover without reaching into the SVG. */
  onReadingTap?: (r: Reading, anchorRect: DOMRect) => void;
  // ── Cycle 57 ─────────────────────────────────────────────────────
  /** When true, the chart renders its own lightweight tooltip on
   *  hover/tap of any reading dot, treatment marker, or alert ring,
   *  showing minimum value/time info. Opt-in so the History screen
   *  stays byte-identical (it doesn't pass this prop). When both
   *  `showTooltips` and `onReadingTap` are set, the dot tap shows the
   *  tooltip and **does not** call `onReadingTap` — the tooltip is
   *  the cycle-57 interaction at the dot. */
  showTooltips?: boolean;
  // ── Cycle 57.8 / 208 ─────────────────────────────────────────────
  /** A short label rendered inside the chart's top-right corner as a
   *  SOLID green partition pill — confirms which time window the chart is
   *  showing ("2h", "6h", "12h", "24h", "7d", "30d"). Opt-in. Cycle 208
   *  renamed this from `currentWindowLabel` and consolidated it into the
   *  single green corner pill rendered on every chart instance that passes
   *  it (NowScreen, HistoryScreen, TrendSheet). */
  partitionLabel?: string;
  /** When true, vertical dotted midnight markers + a day-name label
   *  are drawn inside the visible window. Default `true` when the
   *  visible window spans > 24h; opt-out by passing `false`. Opt-in
   *  via the same `showTooltips` family by passing it explicitly. */
  showDayMarkers?: boolean;
  // ── Cycle 65 ─────────────────────────────────────────────────────
  /** Enable two-finger pinch-to-zoom on top of the existing single-
   *  finger pan. Pinch mutates the visible slice width about the
   *  gesture midpoint; the time under the user's fingers stays put.
   *  Clamped to [MIN_SLICE_MS, fullSpan]. Opt-in — surfaces that
   *  shouldn't allow runtime zoom (the History screen pre-fill-and-
   *  fullscreen branch, future surfaces) just leave it off. */
  pinchZoom?: boolean;
  // ── Cycle 180 ────────────────────────────────────────────────────
  /** Opt-in forward projection (Nightscout-style trend cone), drawn ONLY
   *  by the web Dashboard chart. When true, a short straight-line trend is
   *  extrapolated `projectMinutes` ahead of the latest reading as a dotted
   *  centre line inside a widening uncertainty cone, zone-coloured, ending
   *  in a faded predicted value + a "projected · not for dosing" label. To
   *  keep the geometry stable while panning, the x-domain reserves the
   *  horizon whenever this is on, but the cone is DRAWN only on the live
   *  edge (never on a scrolled-back/historical window — a past day has no
   *  future). DEFAULT OFF: kiosk + mobile (and every non-Dashboard web
   *  call site) pass nothing, so the chart is byte-identical to today.
   *  Display-only; exposes no data beyond the readings already shown, and
   *  is explicitly framed as uncertain + not-for-dosing. */
  projectForward?: boolean;
  /** Projection horizon in minutes (default 30). Only meaningful when
   *  `projectForward` is true. */
  projectMinutes?: number;
  // ── Cycle 183 ────────────────────────────────────────────────────
  /** Opt-in inertial flick. When on, a fast single-pointer pan that
   *  lifts off with velocity COASTS to a stop: release velocity is
   *  estimated from the last few move samples and a `requestAnimation
   *  Frame` decay loop keeps advancing `panOffsetMs` with per-frame
   *  friction until it drops below a stop threshold or hits a data edge
   *  (the existing `[0, fullSpan − sliceMs]` clamp). Cancelled on the
   *  next pointerdown/pinch (own rAF id, like the cycle-65.1 zoom anim)
   *  and disabled under `prefers-reduced-motion: reduce` (pan stops on
   *  release). DEFAULT OFF → every existing call site is byte-identical;
   *  enabled only on the kiosk + web Now/History chart instances. */
  flickMomentum?: boolean;
  // ── Cycle 186.2 ──────────────────────────────────────────────────
  /** Opt-in mouse-wheel / trackpad zoom — the smooth desktop equivalent of
   *  two-finger pinch (which a trackpad does NOT emit as two pointers). A
   *  wheel over the plot zooms the visible slice about the cursor's time,
   *  reusing the pinch anchor + `[MIN_SLICE_MS, fullSpan]` clamp. Only acts
   *  when a slice is active (`sliceMs != null`); `preventDefault`s so the
   *  page doesn't scroll while zooming. DEFAULT OFF → kiosk/mobile + every
   *  other call site byte-identical (touch keeps pinch). Enabled on the web
   *  History chart. */
  wheelZoom?: boolean;
}

// viewBox is fixed; the SVG scales to its container width.
const VIEW_W = 960;
const PAD_L = 36;
const PAD_R = 12;
const PAD_T = 10;
const PAD_B = 26;

// Default visible Y range; adapts when readings exceed it.
const Y_MIN_DEFAULT = 40;
const Y_MAX_DEFAULT = 320;
// Hard-clamp on the auto-scaled domain so a single bogus reading
// (e.g. sensor warm-up spike) can't squash the rest of the chart.
const Y_FLOOR = 20;
const Y_CEIL = 600;

// Cumulative pointer travel (px) below which a gesture is a tap, not
// a pan — lets a dot-tap fire cleanly without nudging the slice.
const TAP_SLOP_PX = 8;

// Cycle 65: pinch-zoom lower bound. ~30 min keeps dots from flying
// apart even on Dexcom's 5-minute cadence (6+ dots visible).
const MIN_SLICE_MS = 30 * 60 * 1000;

// Cycle 186.2: wheel-zoom sensitivity. slice *= exp(deltaY * k); a mouse
// notch (~100) ≈ 16%/notch, a trackpad two-finger swipe streams small deltas
// for smooth continuous zoom. deltaY>0 (scroll down) → larger slice → zoom out.
const WHEEL_ZOOM_K = 0.0015;

// Cycle 183: inertial-flick tuning.
const FLICK_LAUNCH_PX_PER_MS = 0.18; // release speed below this → no coast
const FLICK_STOP_PX_PER_MS = 0.04; // coast ends when speed drops below this
const FLICK_FRICTION = 0.94; // velocity retained per ~16.67ms frame
const FLICK_VELOCITY_WINDOW_MS = 90; // sample span used to estimate velocity

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}

export function HistoryChart({
  readings,
  treatments,
  alerts,
  thresholds,
  rangeStartMs,
  rangeEndMs,
  height = 280,
  h24 = false,
  units = "mg/dL",
  panSliceMs,
  showNowLine = true,
  nowMs = Date.now(),
  onReadingTap,
  showTooltips = false,
  partitionLabel,
  showDayMarkers,
  pinchZoom = false,
  projectForward = false,
  projectMinutes = 30,
  flickMomentum = false,
  wheelZoom = false,
}: Props) {
  const W = VIEW_W;
  const H = height;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const wrapRef = useRef<HTMLDivElement | null>(null);

  // ── Cycle 65 / 0.65.6: measure the container's pixel dims so we
  // can correct shape geometry for any non-uniform stretch the
  // viewBox undergoes. The SVG has `preserveAspectRatio="none"` and
  // `width: 100%`. Cycle 65 assumed Y mapped 1:1 because we pass
  // `height={H}` explicitly, but in practice the parent's
  // `.chartContainer > * { flex: 1 }` rule (Now-screen + kiosk
  // History fill) stretches the wrap to flex-fill while the SVG's
  // viewBox height stays H — so the SVG actually paints taller than
  // its viewBox y-axis when flex-filled, and circles painted as
  // ellipses with only x-correction read as vertically-tall ovals.
  // The right fix is to measure BOTH axes (`pxWidth`, `pxHeight`)
  // and correct both: `xCorrect = VIEW_W / pxWidth`,
  // `yCorrect = H / pxHeight`. An ellipse with `rx = r * xCorrect,
  // ry = r * yCorrect` paints as a true circle of screen-radius `r`
  // regardless of either axis stretch.
  const [pxDims, setPxDims] = useState<{ w: number; h: number }>({
    w: VIEW_W,
    h: height,
  });
  // 0.152.1: re-attach the measurement when the empty↔data branch
  // flips (and the empty wrap now carries the ref too). A chart that
  // MOUNTED with zero readings used to bail out of this effect on a
  // null ref and never observe — so when data later arrived into the
  // already-mounted chart (e.g. the TrendSheet's 24h fetch landing),
  // `pxDims` stayed frozen at its render-default and every dot painted
  // as a tall stretched oval until a full remount.
  const isEmpty = readings.length === 0;
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const apply = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      setPxDims((prev) =>
        prev.w === w && prev.h === h ? prev : { w, h },
      );
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isEmpty]);
  const xCorrect = VIEW_W / pxDims.w;
  const yCorrect = H / pxDims.h;

  // ── Cycle 48 / 65: pannable + pinch-zoomable slice state ────────
  // `sliceMs` is the live slice width — seeded from `panSliceMs` and
  // mutated by pinch. `panOffsetMs` shifts the slice into the past
  // from `rangeEndMs`. Both are clamped to the loaded data window.
  const fullSpan = Math.max(1, rangeEndMs - rangeStartMs);
  const [sliceMs, setSliceMs] = useState<number | undefined>(panSliceMs);
  const [panOffsetMs, setPanOffsetMs] = useState(0);

  // Re-seed the slice when the user's window actually changes. The
  // reset key intentionally drops `rangeStartMs` / `rangeEndMs` — the
  // App-level `useNow(15_000)` clock ticks them forward every 15 s on
  // the Now screen, which previously wiped the zoom mid-pinch. Window
  // identity is now `panSliceMs` (the parent's intended slice for the
  // active window) + `fullSpan` (the loaded data window, which only
  // jumps when the chartHours pill changes — a `now` tick shifts both
  // endpoints by the same Δ so `fullSpan` stays constant). A pill
  // switch flips at least one of these even when two windows happen
  // to share a slice width (e.g. mobile 6 h and 12 h both → 1 h).
  useEffect(() => {
    setSliceMs(panSliceMs);
    setPanOffsetMs(0);
  }, [panSliceMs, fullSpan]);

  // When `now` ticks forward (or the parent's range advances for any
  // reason), keep the user's panned-back view anchored on the same
  // wall-clock time instead of letting it slide forward with the
  // clock. `viewEndMs = rangeEndMs - panOffsetMs`; we want viewEndMs
  // constant, so panOffsetMs must grow by the same Δ as rangeEndMs.
  // When the user is at `panOffsetMs === 0` (right edge — "now"),
  // keep tracking "now" so the live edge stays live. Clamped to
  // `[0, fullSpan - sliceMs]` so a tick at the oldest-edge of the
  // loaded data can't strand the view off the data.
  const prevRangeEndMsRef = useRef(rangeEndMs);
  useEffect(() => {
    const prev = prevRangeEndMsRef.current;
    prevRangeEndMsRef.current = rangeEndMs;
    if (prev === rangeEndMs) return;
    if (panOffsetMsRef.current === 0) return;
    const delta = rangeEndMs - prev;
    if (delta === 0) return;
    const slice = sliceMsRef.current ?? 0;
    const max = Math.max(0, fullSpan - slice);
    setPanOffsetMs((o) => Math.min(Math.max(0, o + delta), max));
  }, [rangeEndMs, fullSpan]);

  // Cycle 65: mirror state into refs so gesture handlers reading the
  // "current" slice/offset (e.g. when a pinch ends and a pan picks
  // up where it left off) never see a stale render-time closure.
  const sliceMsRef = useRef<number | undefined>(sliceMs);
  const panOffsetMsRef = useRef<number>(panOffsetMs);
  useEffect(() => {
    sliceMsRef.current = sliceMs;
  }, [sliceMs]);
  useEffect(() => {
    panOffsetMsRef.current = panOffsetMs;
  }, [panOffsetMs]);

  const maxPanOffsetMs =
    sliceMs != null ? Math.max(0, fullSpan - sliceMs) : 0;

  // Visible bounds: the slice when panning, else the full window.
  const viewEndMs = sliceMs != null ? rangeEndMs - panOffsetMs : rangeEndMs;
  const viewStartMs = sliceMs != null ? viewEndMs - sliceMs : rangeStartMs;

  // ── Cycle 65: multi-pointer gesture machinery ───────────────────
  // Live pointer positions, keyed by pointerId. Single-pointer pan
  // and two-pointer pinch both read this map. Pointer count > 0
  // signals an active gesture.
  type Pt = { x: number; y: number };
  const pointersRef = useRef<Map<number, Pt>>(new Map());
  // Snapshot of state at the moment the current gesture started.
  // Re-snapshots whenever the pointer count changes (a pinch starting
  // mid-pan re-anchors against the current sliceMs / panOffsetMs).
  type GestureSnap =
    | { kind: "idle" }
    | {
        kind: "pan";
        startX: number;
        startOffsetMs: number;
        startSliceMs: number;
        moved: boolean;
      }
    | {
        kind: "pinch";
        startDist: number;
        startMidX: number;
        startSliceMs: number;
        startAnchorMs: number;
      };
  const gestureRef = useRef<GestureSnap>({ kind: "idle" });
  // True for the duration of a pan/pinch + the synthesized click —
  // suppresses onReadingTap firing after a swipe that ended on a dot.
  const suppressTapRef = useRef(false);
  // rAF coalescing: one frame's worth of pointermoves collapses to a
  // single React state update so a fast drag doesn't thrash render.
  const rafIdRef = useRef<number | null>(null);

  // Cycle 65.1: separate rAF for the zoom-to-day animation kicked off
  // by a day-pill tap. Tracked independently so a gesture cancelling
  // the animation doesn't fight a still-pending pan/zoom frame.
  const zoomAnimRef = useRef<number | null>(null);
  const cancelZoomAnim = () => {
    if (zoomAnimRef.current != null) {
      cancelAnimationFrame(zoomAnimRef.current);
      zoomAnimRef.current = null;
    }
  };

  // Cycle 183: inertial flick — a dedicated rAF id (cancelled on the next
  // pointerdown/pinch, like the cycle-65.1 zoom anim) and a short ring of
  // recent single-pointer samples used to estimate release velocity.
  const momentumRafRef = useRef<number | null>(null);
  const panSamplesRef = useRef<Array<{ t: number; x: number }>>([]);
  const cancelMomentum = () => {
    if (momentumRafRef.current != null) {
      cancelAnimationFrame(momentumRafRef.current);
      momentumRafRef.current = null;
    }
  };

  useEffect(() => {
    // Clean up any in-flight animation on unmount.
    return () => {
      cancelZoomAnim();
      cancelMomentum();
    };
  }, []);

  const panEnabled = sliceMs != null && maxPanOffsetMs > 0;
  // Gesture handlers attach whenever pan OR pinch is reachable.
  const gestureEnabled = panEnabled || (pinchZoom && sliceMs != null);

  // Cycle 186.2: mouse-wheel / trackpad-pinch zoom (desktop's smooth zoom).
  // Attached as a NON-passive native listener so `preventDefault()` can stop
  // the page from scrolling while we own the gesture. Reads the live slice/
  // offset from refs, zooms about the cursor's time using the same anchor +
  // clamp math the pinch path uses, and re-binds only when the data window
  // (rangeEndMs / fullSpan) changes — infrequent on History.
  useEffect(() => {
    if (!wheelZoom) return;
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const curSlice = sliceMsRef.current;
      if (curSlice == null) return; // not in a zoomable (sliced) window
      e.preventDefault();
      cancelZoomAnim();
      cancelMomentum();
      const rect = el.getBoundingClientRect();
      const pxW = rect.width > 0 ? rect.width : VIEW_W;
      const localX = e.clientX - rect.left;
      const curOffset = panOffsetMsRef.current;
      // Time under the cursor, held fixed across the zoom.
      const anchorMs =
        rangeEndMs - curOffset - curSlice + (localX / pxW) * curSlice;
      const newSlice = Math.min(
        Math.max(MIN_SLICE_MS, curSlice * Math.exp(e.deltaY * WHEEL_ZOOM_K)),
        fullSpan,
      );
      const newMaxOff = Math.max(0, fullSpan - newSlice);
      const newOffset = Math.min(
        Math.max(
          0,
          rangeEndMs - newSlice - anchorMs + (localX / pxW) * newSlice,
        ),
        newMaxOff,
      );
      setSliceMs(newSlice);
      setPanOffsetMs(newOffset);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [wheelZoom, rangeEndMs, fullSpan]);

  // Cycle 57: lightweight tooltip state — owned by the chart so the
  // History screen never sees it (it doesn't pass `showTooltips`).
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    body: string;
  } | null>(null);
  // Dismiss the tooltip the instant a pan begins so it doesn't lag
  // the slice under the finger.
  const dismissTooltip = () => {
    if (tooltip != null) setTooltip(null);
  };

  // Snapshot the starting state for the next gesture from the live
  // pointer map. Called whenever the active pointer count changes
  // (pointerdown adds, pointerup/cancel removes). Reads slice / pan
  // from refs so a pinch-then-pan transition picks up the latest
  // pinch frame instead of the render-time closure.
  const reSnapshot = () => {
    const pts = Array.from(pointersRef.current.values());
    const rect = wrapRef.current?.getBoundingClientRect();
    const pxW = rect && rect.width > 0 ? rect.width : VIEW_W;
    const liveSlice = sliceMsRef.current;
    const liveOffset = panOffsetMsRef.current;
    if (pts.length === 2 && pinchZoom && liveSlice != null) {
      const [a, b] = pts as [Pt, Pt];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      // Pinch midpoint relative to the chart's bounding box.
      const midClientX = (a.x + b.x) / 2;
      const midLocalX = midClientX - (rect ? rect.left : 0);
      // Time under the midpoint at the moment the pinch starts —
      // we'll anchor zoom so this time stays put under the fingers.
      const anchorMs =
        rangeEndMs - liveOffset - liveSlice +
        (midLocalX / Math.max(1, pxW)) * liveSlice;
      gestureRef.current = {
        kind: "pinch",
        startDist: Math.max(1, dist),
        startMidX: midLocalX,
        startSliceMs: liveSlice,
        startAnchorMs: anchorMs,
      };
      suppressTapRef.current = true;
      dismissTooltip();
      return;
    }
    if (pts.length === 1 && (panEnabled || liveSlice != null)) {
      const [a] = pts as [Pt];
      gestureRef.current = {
        kind: "pan",
        startX: a.x,
        startOffsetMs: liveOffset,
        startSliceMs: liveSlice ?? 0,
        moved: false,
      };
      return;
    }
    gestureRef.current = { kind: "idle" };
  };

  // Apply the active gesture against current pointer positions.
  // Called from rAF; reads `gestureRef` + `pointersRef`, writes
  // panOffsetMs / sliceMs once per frame.
  const applyGesture = () => {
    const g = gestureRef.current;
    const pts = Array.from(pointersRef.current.values());
    const rect = wrapRef.current?.getBoundingClientRect();
    const pxW = rect && rect.width > 0 ? rect.width : VIEW_W;
    if (g.kind === "pan" && pts.length === 1) {
      // Cycle 49 invert: data follows the finger.
      const dx = pts[0]!.x - g.startX;
      if (!g.moved && Math.abs(dx) < TAP_SLOP_PX) return;
      if (!g.moved) {
        g.moved = true;
        suppressTapRef.current = true;
        dismissTooltip();
        try {
          // Best-effort capture so a fast drag past the chart edge
          // keeps the gesture alive.
          const id = Array.from(pointersRef.current.keys())[0];
          if (id != null) wrapRef.current?.setPointerCapture(id);
        } catch {
          /* ignore */
        }
      }
      const slice = g.startSliceMs;
      const deltaMs = dx * (slice / pxW);
      const maxOff = Math.max(0, fullSpan - slice);
      setPanOffsetMs(
        Math.min(Math.max(0, g.startOffsetMs + deltaMs), maxOff),
      );
      return;
    }
    if (g.kind === "pinch" && pts.length === 2) {
      const [a, b] = pts as [Pt, Pt];
      const dist = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
      const midClientX = (a.x + b.x) / 2;
      const midLocalX = midClientX - (rect ? rect.left : 0);
      // Pinch out (dist > startDist) → slice shrinks → zoom in.
      const ratio = g.startDist / dist;
      const rawSlice = g.startSliceMs * ratio;
      const newSlice = Math.min(
        Math.max(MIN_SLICE_MS, rawSlice),
        fullSpan,
      );
      // Anchor: keep startAnchorMs under midLocalX.
      // viewStart = rangeEndMs - panOffsetMs - sliceMs
      // anchorMs = viewStart + (midLocalX / pxW) * sliceMs
      //  → panOffsetMs = rangeEndMs - sliceMs - anchorMs + (midLocalX/pxW)*sliceMs
      const newOffsetRaw =
        rangeEndMs -
        newSlice -
        g.startAnchorMs +
        (midLocalX / Math.max(1, pxW)) * newSlice;
      const newMaxOff = Math.max(0, fullSpan - newSlice);
      const newOffset = Math.min(Math.max(0, newOffsetRaw), newMaxOff);
      setSliceMs(newSlice);
      setPanOffsetMs(newOffset);
      return;
    }
  };

  const scheduleFrame = () => {
    if (rafIdRef.current != null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      applyGesture();
    });
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!gestureEnabled) return;
    // A new gesture starting mid-zoom-animation or mid-flick supersedes
    // it immediately so the slice/offset don't keep sliding under the
    // finger (cycle-65.1 cancel pattern, extended to flick momentum).
    cancelZoomAnim();
    cancelMomentum();
    panSamplesRef.current = [];
    suppressTapRef.current = false;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    reSnapshot();
  };

  // Cycle 65.1: zoom-to-day animation. A tap on any day-label pill
  // animates sliceMs → 24h and panOffsetMs → the offset that anchors
  // the visible window on that 24h day. easeOutCubic over ~420 ms;
  // duration tuned to feel snappy on the Pi without skipping frames.
  // Pan offset is clamped so picking "today" (whose end ≥ rangeEndMs)
  // doesn't leave a blank gutter past the loaded data.
  const animateToDay = (midnightMs: number) => {
    if (sliceMs == null) return;
    const DAY_MS = 24 * 60 * 60 * 1000;
    const targetSlice = Math.min(Math.max(MIN_SLICE_MS, DAY_MS), fullSpan);
    const targetEndMs = midnightMs + targetSlice;
    const targetOffsetRaw = rangeEndMs - targetEndMs;
    const targetMaxOff = Math.max(0, fullSpan - targetSlice);
    const targetOffset = Math.min(
      Math.max(0, targetOffsetRaw),
      targetMaxOff,
    );
    const startSlice = sliceMs;
    const startOffset = panOffsetMs;
    if (
      Math.abs(targetSlice - startSlice) < 1 &&
      Math.abs(targetOffset - startOffset) < 1
    ) {
      // Already there — nothing to animate.
      return;
    }
    cancelZoomAnim();
    const t0 = performance.now();
    const dur = 420;
    const easeOutCubic = (x: number) => 1 - Math.pow(1 - x, 3);
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / dur);
      const eased = easeOutCubic(k);
      const ns = startSlice + (targetSlice - startSlice) * eased;
      const no = startOffset + (targetOffset - startOffset) * eased;
      setSliceMs(ns);
      setPanOffsetMs(no);
      if (k < 1) {
        zoomAnimRef.current = requestAnimationFrame(tick);
      } else {
        zoomAnimRef.current = null;
      }
    };
    zoomAnimRef.current = requestAnimationFrame(tick);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // Cycle 183: record a short ring of single-pointer samples so a
    // pointerup can estimate the release velocity. Two-finger gestures
    // (pinch) never record — velocity is meaningless mid-pinch.
    if (flickMomentum && pointersRef.current.size === 1) {
      const samples = panSamplesRef.current;
      samples.push({ t: performance.now(), x: e.clientX });
      if (samples.length > 6) samples.shift();
    }
    scheduleFrame();
  };

  // Cycle 183: on a single-finger pan release, coast with friction. Reads
  // the recent sample ring for velocity, then advances panOffsetMs each
  // frame (clamped to the data edges) until it stops or hits an edge.
  const maybeStartMomentum = () => {
    const slice = sliceMsRef.current;
    if (slice == null || prefersReducedMotion()) return;
    const samples = panSamplesRef.current;
    if (samples.length < 2) return;
    const last = samples[samples.length - 1]!;
    // Earliest sample within the velocity window of the release.
    let first = samples[0]!;
    for (const s of samples) {
      if (last.t - s.t <= FLICK_VELOCITY_WINDOW_MS) {
        first = s;
        break;
      }
    }
    const dt = last.t - first.t;
    if (dt <= 0) return;
    let velPxPerMs = (last.x - first.x) / dt;
    if (Math.abs(velPxPerMs) < FLICK_LAUNCH_PX_PER_MS) return;

    const rect = wrapRef.current?.getBoundingClientRect();
    const pxW = rect && rect.width > 0 ? rect.width : VIEW_W;
    let curOffset = panOffsetMsRef.current;
    let prevT = performance.now();

    const step = (now: number) => {
      const frameDt = Math.min(40, now - prevT);
      prevT = now;
      const slc = sliceMsRef.current ?? slice;
      const maxOff = Math.max(0, fullSpan - slc);
      // Same px→offset mapping the drag uses: deltaMs = dx * (slice/pxW).
      const offsetDelta = velPxPerMs * frameDt * (slc / pxW);
      const next = Math.min(Math.max(0, curOffset + offsetDelta), maxOff);
      const hitEdge =
        (next <= 0 && offsetDelta < 0) || (next >= maxOff && offsetDelta > 0);
      curOffset = next;
      setPanOffsetMs(next);
      velPxPerMs *= Math.pow(FLICK_FRICTION, frameDt / 16.667);
      if (Math.abs(velPxPerMs) < FLICK_STOP_PX_PER_MS || hitEdge) {
        momentumRafRef.current = null;
        return;
      }
      momentumRafRef.current = requestAnimationFrame(step);
    };
    momentumRafRef.current = requestAnimationFrame(step);
  };

  const endGesture = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    // Cycle 183: snapshot whether this was a real pan BEFORE we reset the
    // gesture, so a clean single-finger release can coast.
    const wasPan =
      gestureRef.current.kind === "pan" && gestureRef.current.moved;
    pointersRef.current.delete(e.pointerId);
    try {
      wrapRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (pointersRef.current.size === 0) {
      gestureRef.current = { kind: "idle" };
      // Coast only when the last finger lifts off a genuine pan.
      if (flickMomentum && wasPan) maybeStartMomentum();
    } else {
      // A pinch becoming a pan, or vice versa — re-anchor.
      reSnapshot();
    }
  };

  const handleReadingTap = (
    e: React.MouseEvent<SVGCircleElement | SVGEllipseElement>,
    r: Reading,
    cx: number,
    cy: number,
  ) => {
    if (suppressTapRef.current) {
      // A pan just ended; swallow the synthesized click once.
      suppressTapRef.current = false;
      return;
    }
    if (showTooltips) {
      const ts = new Date(r.timestamp).getTime();
      setTooltip({
        x: cx,
        y: cy,
        body: `${displayValue(r.sgv, units)} ${units} · ${fmtTime(ts, { h24 })}`,
      });
      return;
    }
    if (!onReadingTap) return;
    onReadingTap(r, e.currentTarget.getBoundingClientRect());
  };

  // Shared tap handler for treatment + alert markers.
  const handleMarkerTap = (cx: number, cy: number, body: string) => {
    if (suppressTapRef.current) {
      suppressTapRef.current = false;
      return;
    }
    setTooltip({ x: cx, y: cy, body });
  };

  // Y-axis domain.
  const { yMin, yMax, yTicks } = useMemo(() => {
    let lo = Y_MIN_DEFAULT;
    let hi = Y_MAX_DEFAULT;
    for (const r of readings) {
      if (r.sgv < lo) lo = r.sgv;
      if (r.sgv > hi) hi = r.sgv;
    }
    for (const a of alerts) {
      if (a.sgv < lo) lo = a.sgv;
      if (a.sgv > hi) hi = a.sgv;
    }
    lo = Math.max(Y_FLOOR, Math.floor((lo - 10) / 20) * 20);
    hi = Math.min(Y_CEIL, Math.ceil((hi + 10) / 20) * 20);
    if (hi - lo < 60) hi = lo + 60;

    const baseTicks = [
      thresholds.urgent_low,
      thresholds.target_low,
      thresholds.target_high,
      thresholds.urgent_high,
    ].filter((t) => t > lo && t < hi);
    const ticks = [...new Set([lo, ...baseTicks, hi])].sort((a, b) => a - b);
    return { yMin: lo, yMax: hi, yTicks: ticks };
  }, [readings, alerts, thresholds]);

  // Cycle 180: when the forward projection is enabled, reserve the horizon
  // to the RIGHT of the data so the cone has room. `projReserveMs` is a
  // constant per instance (projectForward never toggles at runtime), so the
  // scale stays stable while panning and — crucially — is exactly 0 when
  // the prop is off, keeping kiosk/mobile byte-identical.
  const projReserveMs = projectForward ? projectMinutes * 60 * 1000 : 0;
  const domainEndMs = viewEndMs + projReserveMs;
  const span = Math.max(1, domainEndMs - viewStartMs);
  const xScale = (t: number) => PAD_L + ((t - viewStartMs) / span) * innerW;
  const yScale = (v: number) =>
    PAD_T + (1 - (v - yMin) / (yMax - yMin)) * innerH;
  // Clamp a y-pixel into the plot band so a steep projection can't draw
  // outside the chart (it pins at the edge, still conveying direction).
  const clampY = (y: number) => Math.min(H - PAD_B, Math.max(PAD_T, y));

  const path = useMemo(() => {
    if (readings.length < 2) return "";
    return readings
      .map((r, i) => {
        const x = xScale(new Date(r.timestamp).getTime()).toFixed(1);
        const y = yScale(r.sgv).toFixed(1);
        return `${i === 0 ? "M" : "L"}${x} ${y}`;
      })
      .join(" ");
    // Cycle 208: `H`/`innerH` (the `height` prop) MUST be in the deps. The
    // reading-dot centres are computed inline at render via `yScale`, but the
    // trend-line path is memoized — and `yScale` closes over `innerH` (= H -
    // PAD_T - PAD_B). On the FIRST open of a chart, the parent's measured-fill
    // height lands a frame AFTER the readings (NowScreen `chartHeight`,
    // TrendSheet `plotHeight`, kiosk/web History fill), so the dots re-laid
    // out against the new height while this memo returned its stale path from
    // the old height — the line painted a fixed distance ABOVE/BELOW the dots
    // until a close/reopen (which mounts with a settled height). Re-keying on
    // `height` makes the line and the dots share the same y-scale on the first
    // painted data frame on every surface. (`innerW`/`W` are fixed, so X never
    // diverged — the offset was always vertical.)
  }, [readings, viewStartMs, viewEndMs, yMin, yMax, height]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cycle 180: least-squares trend over the last ~4–6 readings, used by the
  // opt-in forward projection. A straight line earns no more than a straight
  // line — `rmse` (the fit's residual scatter) drives how fast the cone
  // widens, so a noisy recent trace projects a visibly less certain cone.
  // Cycle 186: the math is extracted to lib/projection.ts so the Dashboard's
  // Live focus panel hero ("in 30 min ~X") consumes the SAME model and can't
  // disagree with the cone. Null (no projection) when disabled / too few pts.
  const projectionModel = useMemo(
    () => (projectForward ? computeProjectionModel(readings) : null),
    [projectForward, readings],
  );

  // Cycle 57.8: midnight-boundary day markers.
  const visibleSpanMs = viewEndMs - viewStartMs;
  const dayMarkersEnabled =
    showDayMarkers === undefined
      ? visibleSpanMs > 24 * 60 * 60 * 1000
      : showDayMarkers;
  const dayMarkers = useMemo(() => {
    if (!dayMarkersEnabled) return [];
    const out: Array<{ t: number; label: string }> = [];
    const start = new Date(viewStartMs);
    const firstMidnight = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + 1,
      0,
      0,
      0,
      0,
    ).getTime();
    const DAY_MS = 24 * 60 * 60 * 1000;
    for (let t = firstMidnight, i = 0; t <= viewEndMs && i < 40; t += DAY_MS, i++) {
      const d = new Date(t);
      const label =
        visibleSpanMs > 8 * 24 * 60 * 60 * 1000
          ? `${d.getMonth() + 1}/${d.getDate()}`
          : d.toLocaleDateString(undefined, { weekday: "short" });
      out.push({ t, label });
    }
    return out;
  }, [dayMarkersEnabled, viewStartMs, viewEndMs, visibleSpanMs]);

  if (readings.length === 0) {
    return (
      // The ref rides the empty wrap too (0.152.1) so the measurement
      // is live from the first mount even when data arrives later.
      <div ref={wrapRef} className={styles.wrap}>
        <div className={styles.empty} style={{ minHeight: height }}>
          No readings in this window
        </div>
      </div>
    );
  }

  const lowY = yScale(thresholds.target_low);
  const highY = yScale(thresholds.target_high);
  const ulY = yScale(thresholds.urgent_low);
  const uhY = yScale(thresholds.urgent_high);
  const last = readings[readings.length - 1]!;
  const lastZone: Zone = zoneOf(last.sgv, thresholds);

  const midMs = (viewStartMs + viewEndMs) / 2;
  const xLabels: Array<{ x: number; t: number }> = [
    { x: PAD_L, t: viewStartMs },
    { x: PAD_L + innerW / 2, t: midMs },
    { x: PAD_L + innerW, t: viewEndMs },
  ];

  const yPct = (v: number) => (yScale(v) / H) * 100;
  const xPct = (t: number) => (xScale(t) / W) * 100;

  const nowVisible =
    showNowLine && nowMs >= viewStartMs && nowMs <= viewEndMs;
  const nowX = nowVisible ? xScale(nowMs) : 0;

  // Cycle 180: forward-projection geometry. Drawn ONLY at the live edge —
  // never on a scrolled-back/historical window (a past day has no future),
  // and never off a stale last reading (a quiet sensor can't speak to
  // "now"). Everything routes through the existing x/y scales; the cone
  // pins inside the plot band via `clampY`.
  const projection = (() => {
    const m = projectionModel;
    if (!m) return null;
    const atLiveEdge = sliceMs == null || panOffsetMs === 0;
    if (!atLiveEdge) return null;
    if (isProjectionStale(m, nowMs, projectMinutes)) return null;

    const horizon = projectMinutes;
    const center = (mins: number) => projectedCenter(m, mins);
    // Cone half-width grows from a small floor to a cap, scaled by the
    // fit's scatter (`rmse`) and the trend's steepness. A presentation
    // heuristic — deliberately wide, NEVER a calibrated confidence band.
    const maxHalf = Math.min(
      70,
      Math.max(12, m.rmse + Math.abs(m.slope) * horizon * 0.4 + 8),
    );
    const half = (mins: number) => 4 + (maxHalf - 4) * (mins / horizon);

    const STEP = 5;
    const samples: { x: number; cY: number; uY: number; lY: number }[] = [];
    for (let mins = 0; mins <= horizon + 0.001; mins += STEP) {
      const t = m.t0 + mins * 60_000;
      const c = center(mins);
      const hw = half(mins);
      samples.push({
        x: xScale(t),
        cY: clampY(yScale(c)),
        uY: clampY(yScale(c + hw)),
        lY: clampY(yScale(c - hw)),
      });
    }

    const conePath =
      samples
        .map((s, i) => `${i === 0 ? "M" : "L"}${s.x.toFixed(1)} ${s.uY.toFixed(1)}`)
        .join(" ") +
      " " +
      [...samples]
        .reverse()
        .map((s) => `L${s.x.toFixed(1)} ${s.lY.toFixed(1)}`)
        .join(" ") +
      " Z";
    const centerPath = samples
      .map((s, i) => `${i === 0 ? "M" : "L"}${s.x.toFixed(1)} ${s.cY.toFixed(1)}`)
      .join(" ");

    const predVal = predictedValue(m, horizon);
    const predZone: Zone = zoneOf(predVal, thresholds);
    const end = samples[samples.length - 1]!;
    return {
      conePath,
      centerPath,
      color: zoneColor(predZone),
      predVal,
      endX: end.x,
      endY: end.cY,
    };
  })();

  return (
    <div
      ref={wrapRef}
      className={styles.wrap}
      style={{
        height: H,
        // Cycle 65: when any gesture is reachable, block native page
        // scroll + browser pinch-zoom inside the chart so JS can
        // own the gesture. Previously was `pan-y` for pan-only.
        touchAction: gestureEnabled ? "none" : undefined,
        cursor: panEnabled ? "grab" : undefined,
      }}
      onPointerDown={gestureEnabled ? onPointerDown : undefined}
      onPointerMove={gestureEnabled ? onPointerMove : undefined}
      onPointerUp={gestureEnabled ? endGesture : undefined}
      onPointerCancel={gestureEnabled ? endGesture : undefined}
      onClick={
        showTooltips ? () => setTooltip(null) : undefined
      }
    >
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ display: "block" }}
      >
        {/* In-range band */}
        <rect
          x={PAD_L}
          y={highY}
          width={innerW}
          height={lowY - highY}
          fill="var(--zone-ok-soft)"
        />

        {/* Threshold lines */}
        <line
          x1={PAD_L}
          y1={lowY}
          x2={PAD_L + innerW}
          y2={lowY}
          stroke="var(--ink-3)"
          strokeOpacity={0.5}
          strokeDasharray="2 4"
        />
        <line
          x1={PAD_L}
          y1={highY}
          x2={PAD_L + innerW}
          y2={highY}
          stroke="var(--ink-3)"
          strokeOpacity={0.5}
          strokeDasharray="2 4"
        />
        <line
          x1={PAD_L}
          y1={ulY}
          x2={PAD_L + innerW}
          y2={ulY}
          stroke="var(--zone-urgent)"
          strokeOpacity={0.25}
          strokeDasharray="2 4"
        />
        <line
          x1={PAD_L}
          y1={uhY}
          x2={PAD_L + innerW}
          y2={uhY}
          stroke="var(--zone-urgent)"
          strokeOpacity={0.25}
          strokeDasharray="2 4"
        />

        {/* Trend line */}
        <path
          d={path}
          fill="none"
          stroke="var(--ink-3)"
          strokeWidth={1.2}
          strokeOpacity={0.65}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Cycle 180: opt-in forward projection (web Dashboard only).
            Widening uncertainty cone + dotted centre line + faded
            predicted dot, zone-coloured. Explicitly uncertain; the
            "projected · not for dosing" label rides the axis layer. */}
        {projection && (
          <g aria-hidden="true">
            <path
              d={projection.conePath}
              fill={`color-mix(in srgb, ${projection.color} 14%, transparent)`}
              stroke="none"
            />
            <path
              d={projection.centerPath}
              fill="none"
              stroke={projection.color}
              strokeOpacity={0.7}
              strokeWidth={1.6}
              strokeDasharray="2 4"
              strokeLinecap="round"
            />
            <ellipse
              cx={projection.endX}
              cy={projection.endY}
              rx={4 * xCorrect}
              ry={4 * yCorrect}
              fill={projection.color}
              fillOpacity={0.5}
              stroke="var(--bg-sunk)"
              strokeWidth={1}
            />
          </g>
        )}

        {/* "now" dashed marker */}
        {nowVisible && (
          <line
            x1={nowX}
            y1={PAD_T}
            x2={nowX}
            y2={H - PAD_B}
            stroke="var(--ink-3)"
            strokeOpacity={0.6}
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
        )}

        {/* Cycle 57.8: midnight day-boundary dotted lines */}
        {dayMarkers.map((m) => {
          const x = xScale(m.t);
          return (
            <line
              key={`dm-${m.t}`}
              x1={x}
              y1={PAD_T}
              x2={x}
              y2={H - PAD_B}
              stroke="var(--ink-3)"
              strokeOpacity={0.35}
              strokeWidth={1}
              strokeDasharray="2 5"
            />
          );
        })}

        {/* Per-reading dots. Cycle 65: tap-target is an <ellipse>
            whose rx is x-corrected so the tap circle matches the
            visible reading dot on every variant width. */}
        {readings.map((r) => {
          const ts = new Date(r.timestamp).getTime();
          if (ts < viewStartMs || ts > viewEndMs) return null;
          const cx = xScale(ts);
          const cy = yScale(r.sgv);
          return (
            <g key={r.id}>
              <MarkerGlyph
                kind="reading"
                cx={cx}
                cy={cy}
                color={zoneColor(zoneOf(r.sgv, thresholds))}
                readingRadius={sliceMs != null ? 5 : undefined}
                xCorrect={xCorrect}
                yCorrect={yCorrect}
              />
              {(onReadingTap || showTooltips) && (
                <ellipse
                  cx={cx}
                  cy={cy}
                  rx={10 * xCorrect}
                  ry={10 * yCorrect}
                  fill="transparent"
                  style={{ cursor: "pointer", pointerEvents: "all" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReadingTap(e, r, cx, cy);
                  }}
                  onMouseEnter={() => {
                    if (!showTooltips) return;
                    const tsm = new Date(r.timestamp).getTime();
                    setTooltip({
                      x: cx,
                      y: cy,
                      body: `${displayValue(r.sgv, units)} ${units} · ${fmtTime(tsm, { h24 })}`,
                    });
                  }}
                  onMouseLeave={() => {
                    if (!showTooltips) return;
                    setTooltip(null);
                  }}
                />
              )}
            </g>
          );
        })}

        {/* Treatment markers */}
        {treatments.map((tr) => {
          const ts = new Date(tr.timestamp).getTime();
          if (ts < viewStartMs || ts > viewEndMs) return null;
          const x = xScale(ts);
          const yBase = H - PAD_B - 2;
          const color = treatmentColor(tr.event_type);
          const glyphCy = yBase - 4;
          return (
            <g key={tr.id}>
              <line
                x1={x}
                y1={PAD_T}
                x2={x}
                y2={yBase}
                stroke={color}
                strokeOpacity={0.18}
                strokeDasharray="1 3"
              />
              <MarkerGlyph
                kind={glyphKindForEvent(tr.event_type)}
                cx={x}
                cy={glyphCy}
                color={color}
                xCorrect={xCorrect}
                yCorrect={yCorrect}
              />
              {showTooltips && (
                <ellipse
                  cx={x}
                  cy={glyphCy}
                  rx={10 * xCorrect}
                  ry={10 * yCorrect}
                  fill="transparent"
                  style={{ cursor: "pointer", pointerEvents: "all" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkerTap(
                      x,
                      glyphCy,
                      treatmentTooltipBody(tr, h24),
                    );
                  }}
                  onMouseEnter={() =>
                    setTooltip({
                      x,
                      y: glyphCy,
                      body: treatmentTooltipBody(tr, h24),
                    })
                  }
                  onMouseLeave={() => setTooltip(null)}
                />
              )}
            </g>
          );
        })}

        {/* Alert markers */}
        {alerts.map((a) => {
          const ts = new Date(a.triggered_at).getTime();
          if (ts < viewStartMs || ts > viewEndMs) return null;
          const x = xScale(ts);
          const y = yScale(a.sgv);
          const fill =
            a.level === "urgent_low" || a.level === "urgent_high"
              ? "var(--zone-urgent)"
              : "var(--zone-warn)";
          const alertBody = `${alertLevelLabel(a.level)} · ${fmtTime(ts, {
            h24,
          })}`;
          return (
            <g key={a.id}>
              <MarkerGlyph
                kind="alert"
                cx={x}
                cy={y}
                color={fill}
                opacity={a.acknowledged_at ? 0.55 : 1}
                xCorrect={xCorrect}
                yCorrect={yCorrect}
              />
              {showTooltips && (
                <ellipse
                  cx={x}
                  cy={y}
                  rx={10 * xCorrect}
                  ry={10 * yCorrect}
                  fill="transparent"
                  style={{ cursor: "pointer", pointerEvents: "all" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkerTap(x, y, alertBody);
                  }}
                  onMouseEnter={() => setTooltip({ x, y, body: alertBody })}
                  onMouseLeave={() => setTooltip(null)}
                />
              )}
            </g>
          );
        })}

        {/* Latest dot — also x-corrected so it reads round. */}
        {(() => {
          const lcx = xScale(new Date(last.timestamp).getTime());
          const lcy = yScale(last.sgv);
          const r = sliceMs != null ? 7 : 4.5;
          return (
            <ellipse
              cx={lcx}
              cy={lcy}
              rx={r * xCorrect}
              ry={r * yCorrect}
              fill={zoneColor(lastZone)}
              stroke="var(--bg-sunk)"
              strokeWidth={1.5}
            />
          );
        })()}
      </svg>

      {/* HTML axis labels overlaid on the SVG. */}
      <div className={styles.axisLayer} aria-hidden="true">
        {yTicks.map((v) => (
          <span
            key={`y-${v}`}
            className={styles.yLabel}
            style={{ top: `${yPct(v)}%` }}
          >
            {displayValue(v, units)}
          </span>
        ))}
        {xLabels.map((l, i) => (
          <span
            key={`x-${i}`}
            className={styles.xLabel}
            style={{
              left: `${xPct(l.t)}%`,
              transform:
                i === 0
                  ? "translate(0, 0)"
                  : i === xLabels.length - 1
                  ? "translate(-100%, 0)"
                  : "translate(-50%, 0)",
            }}
          >
            {fmtTime(l.t, { h24 })}
          </span>
        ))}
        {nowVisible && (
          <span
            className={styles.nowLabel}
            style={{
              left: `${xPct(nowMs)}%`,
              top: `${(PAD_T / H) * 100}%`,
              // Cycle 452.1: shown whole — near an edge the label sits beside
              // the line (right of it at the left edge, left of it at the
              // right edge) instead of centring on it and running off.
              transform:
                xPct(nowMs) > 95
                  ? "translate(-100%, 0)"
                  : xPct(nowMs) < 5
                    ? "translate(0, 0)"
                    : undefined,
            }}
          >
            now
          </span>
        )}
        {/* Cycle 180: projected value + the mandatory not-for-dosing
            framing, anchored at the cone's end. Right-aligned so it never
            runs off the chart's right edge. */}
        {projection && (
          <span
            className={styles.projLabel}
            style={{
              left: `${(projection.endX / W) * 100}%`,
              top: `${(projection.endY / H) * 100}%`,
            }}
          >
            <span className={`${styles.projValue} tnum`}>
              ~{displayValue(projection.predVal, units)}
            </span>
            <span className={styles.projNote}>projected · not for dosing</span>
          </span>
        )}
        {dayMarkers.map((m) => (
          // Cycle 65.1: day pills are now buttons — tap animates a
          // zoom into that 24-hour window. Only meaningful when the
          // chart is in pannable mode (sliceMs != null); otherwise
          // the click is a no-op via the disabled attribute (still
          // tappable but visually no different).
          <button
            key={`dml-${m.t}`}
            type="button"
            className={styles.dayLabel}
            style={{
              left: `${xPct(m.t)}%`,
              top: `${(PAD_T / H) * 100}%`,
            }}
            disabled={sliceMs == null}
            onClick={(e) => {
              e.stopPropagation();
              animateToDay(m.t);
            }}
            aria-label={`Zoom to ${m.label}`}
          >
            {m.label}
          </button>
        ))}
        {partitionLabel ? (
          <span className={styles.windowPill}>{partitionLabel}</span>
        ) : null}
        {tooltip ? (
          (() => {
            const xPctValue = (tooltip.x / W) * 100;
            const yPctValue = (tooltip.y / H) * 100;
            const flipRight = xPctValue > 70;
            const flipDown = yPctValue < 22;
            const translateX = flipRight ? "-100%" : "0";
            const offsetX = flipRight ? -10 : 10;
            const offsetY = flipDown ? 12 : -12;
            const translateY = flipDown ? "0" : "-100%";
            return (
              <span
                className={styles.tooltip}
                style={{
                  left: `calc(${xPctValue}% + ${offsetX}px)`,
                  top: `calc(${yPctValue}% + ${offsetY}px)`,
                  transform: `translate(${translateX}, ${translateY})`,
                }}
              >
                {tooltip.body}
              </span>
            );
          })()
        ) : null}
      </div>
    </div>
  );
}
